'use client';

import React, { useEffect, useState, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  reportesService,
  ColocacionData,
  ColocacionTabularData,
} from '@/lib/api/reportesService';
import { Button } from '@/components/ui/Button';
import ErrorMessage from '@/components/common/ErrorMessage';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

type AccionType = 'Graficar' | 'Tabular';

const ColocacionManagement: React.FC = () => {
  const [dataGrafica, setDataGrafica] = useState<ColocacionData | null>(null);
  const [dataTabular, setDataTabular] = useState<ColocacionTabularData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accion, setAccion] = useState<AccionType>('Graficar');
  const [fechaInicio, setFechaInicio] = useState(() => {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth();
    return new Date(y, m - 2, 1).toISOString().split('T')[0];
  });
  const [fechaFin, setFechaFin] = useState(() => {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth();
    return new Date(y, m + 1, 0).toISOString().split('T')[0];
  });
  const [frecuencia, setFrecuencia] = useState('2');
  const printRefGrafica = useRef<HTMLDivElement>(null);
  const printRefTabular = useRef<HTMLDivElement>(null);

  const handlePrint = useCallback(() => {
    const printStyles = `
      body { font-family: system-ui, -apple-system, sans-serif; font-size: 12px; padding: 16px; color: #111; background: white; }
      table { border-collapse: collapse; width: 100%; margin-bottom: 16px; }
      th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
      th { background: #f3f4f6; font-weight: 600; }
      .text-right { text-align: right; }
      h1, h2, h3, h4 { margin: 8px 0; }
      img { max-width: 100%; height: auto; display: block; margin: 16px 0; }
      .header { margin-bottom: 16px; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; }
    `;
    const now = new Date();
    const dateStr = now.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });

    if (accion === 'Graficar' && dataGrafica && printRefGrafica.current) {
      const elem = printRefGrafica.current;
      const canvases = elem.querySelectorAll('canvas');
      const clone = elem.cloneNode(true) as HTMLElement;
      const cloneCanvases = Array.from(clone.querySelectorAll('canvas'));
      cloneCanvases.forEach((c, i) => {
        const liveCanvas = canvases[i] as HTMLCanvasElement;
        if (liveCanvas) {
          const img = document.createElement('img');
          img.src = liveCanvas.toDataURL('image/png');
          img.style.maxWidth = '100%';
          img.style.marginBottom = '16px';
          c.parentNode?.replaceChild(img, c);
        }
      });
      const header = `<div class="header"><h1>Colocación - Vista Gráfica</h1><p>${dataGrafica.fechaInicio} - ${dataGrafica.fechaFin} | Generado: ${dateStr}</p></div>`;
      const contenido = header + DOMPurify.sanitize(clone.innerHTML);
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Colocación - Gráficas</title><style>${printStyles}</style></head><body>${contenido}<script>window.onload=function(){window.print();window.close();}<\/script></body></html>`;
      const mywindow = window.open('', '_blank');
      if (!mywindow) return;
      mywindow.document.write(html);
      mywindow.document.close();
    } else if (accion === 'Tabular' && dataTabular && printRefTabular.current) {
      const elem = printRefTabular.current;
      const header = `<div class="header"><h1>Colocación - Vista Tabular</h1><p>Generado: ${dateStr}</p></div>`;
      const contenido = header + DOMPurify.sanitize(elem.innerHTML);
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Colocación - Tabular</title><style>${printStyles}</style></head><body>${contenido}<script>window.onload=function(){window.print();window.close();}<\/script></body></html>`;
      const mywindow = window.open('', '_blank');
      if (!mywindow) return;
      mywindow.document.write(html);
      mywindow.document.close();
    } else if ((accion === 'Graficar' && !dataGrafica) || (accion === 'Tabular' && !dataTabular)) {
      window.alert('No hay datos para imprimir. Consulte primero.');
    } else {
      window.print();
    }
  }, [accion, dataGrafica, dataTabular]);

  const load = useCallback(async (accionOverride?: AccionType) => {
    const accionActual = accionOverride ?? accion;
    if (!fechaInicio || !fechaFin) {
      setError('Las fechas de inicio y fin son requeridas');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const res = await reportesService.getColocacion({
        fechaInicio,
        fechaFin,
        frecuencia,
        accion: accionActual
      });
      if (res.data) {
        setDataGrafica(null);
        setDataTabular(null);
        if (accionActual === 'Tabular') {
          setDataTabular(res.data as ColocacionTabularData);
        } else {
          setDataGrafica(res.data as ColocacionData);
        }
      } else {
        setError(res.error || 'Error al cargar reporte de Colocación');
      }
    } catch (err: any) {
      setError(err.message || 'Error al cargar reporte de Colocación');
    } finally {
      setIsLoading(false);
    }
  }, [fechaInicio, fechaFin, frecuencia, accion]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccion = useCallback((nuevaAccion: AccionType) => {
    setAccion(nuevaAccion);
    load(nuevaAccion);
  }, [load]);

  const formatCurrency = useCallback((value: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }, []);

  const formatPercent = useCallback((value: number, total: number) => {
    if (total === 0) return '0.0%';
    return `${((value / total) * 100).toFixed(1)}%`;
  }, []);

  const formatSeguro = useCallback((value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (num === 0 || isNaN(num)) return 'Sin Seguro';
    return `${num / 1000}K`;
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="no-print flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Colocación</h1>
          <p className="text-sm text-gray-500 mt-1">
            Métricas de colocación de créditos (autorizados y fondeados)
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={accion === 'Graficar' ? 'primary' : 'secondary'}
            onClick={() => handleAccion('Graficar')}
            disabled={isLoading}
          >
            Graficar
          </Button>
          <Button
            variant={accion === 'Tabular' ? 'primary' : 'secondary'}
            onClick={() => handleAccion('Tabular')}
            disabled={isLoading}
          >
            Tabular
          </Button>
          <Button variant="secondary" onClick={handlePrint}>
            Imprimir
          </Button>
        </div>
      </div>

      {error && <ErrorMessage message={error} />}

      <div className="no-print bg-white p-3 sm:p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha inicio</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin</label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frecuencia</label>
            <select
              value={frecuencia}
              onChange={(e) => setFrecuencia(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="1">Diaria</option>
              <option value="2">Mensual</option>
            </select>
          </div>
          <div>
            <Button variant="primary" onClick={() => load()} className="w-full" disabled={isLoading}>
              Consultar
            </Button>
          </div>
        </div>
      </div>

      {accion === 'Graficar' && dataGrafica && (
        <div ref={printRefGrafica}>
          <VistaGrafica data={dataGrafica} formatCurrency={formatCurrency} />
        </div>
      )}

      {accion === 'Tabular' && dataTabular && (
        <div ref={printRefTabular}>
          <VistaTabular data={dataTabular} formatCurrency={formatCurrency} formatPercent={formatPercent} formatSeguro={formatSeguro} />
        </div>
      )}

      <style jsx global>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
};

// Paleta de colores por año (una color por año, no por mes)
const YEAR_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899',
  '#06B6D4', '#84CC16', '#F97316', '#6366F1', '#14B8A6', '#A855F7',
];

// Eje X en modo mensual: siempre ene a dic
const MESES_EJE_X = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Nombres completos de meses para la tabla (columnas enero-diciembre)
const MESES_NOMBRES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const MES_ABBR_TO_NUM: Record<string, number> = {
  ene: 1, jan: 1, feb: 2, mar: 3, abr: 4, apr: 4, may: 5, jun: 6, jul: 7, ago: 8, aug: 8,
  sep: 9, oct: 10, nov: 11, dic: 12, dec: 12,
};

/** Extrae el año de mesLabel (ej. "ene 2024" -> 2024) */
function parseYearFromMesLabel(mesLabel: string | undefined): number | null {
  if (!mesLabel) return null;
  const parts = mesLabel.trim().split(/\s+/);
  const last = parts[parts.length - 1];
  const y = parseInt(last, 10);
  return Number.isFinite(y) && y >= 1900 && y <= 2100 ? y : null;
}

/** Extrae el mes (1-12) de mesLabel (ej. "ene 2024" -> 1). Soporta es/en. */
function parseMonthFromMesLabel(mesLabel: string | undefined): number | null {
  if (!mesLabel) return null;
  const parts = mesLabel.trim().split(/\s+/);
  const abbr = (parts[0] || '').toLowerCase().slice(0, 3);
  const num = MES_ABBR_TO_NUM[abbr];
  return num != null ? num : null;
}

/** Para un año dado, devuelve array de 12 valores (ene=0 .. dic=11) del campo indicado. */
function getValuesByMonthForYear(
  serie: { mesLabel?: string; [k: string]: any }[],
  year: number,
  field: 'cuenta' | 'cuentaAcumulado' | 'montoAutorizado' | 'montoAutorizadoAcumulado' | 'montoTransferencia' | 'montoTransferenciaAcumulado'
): number[] {
  const byMonth: Record<number, number> = {};
  serie.forEach((d) => {
    const y = parseYearFromMesLabel(d.mesLabel);
    const m = parseMonthFromMesLabel(d.mesLabel);
    if (y === year && m != null && m >= 1 && m <= 12) {
      const val = d[field];
      byMonth[m] = Number(val) || 0;
    }
  });
  return MESES_EJE_X.map((_, i) => byMonth[i + 1] ?? 0);
}

/** En modo mensual: colores por año. Retorna { yearToColorIndex, pointColors } para la serie. */
function getYearColors(serie: { mesLabel?: string }[]): { yearToColorIndex: Record<number, number>; pointColors: string[] } {
  const yearToColorIndex: Record<number, number> = {};
  let nextIdx = 0;
  serie.forEach((d) => {
    const y = parseYearFromMesLabel(d.mesLabel);
    if (y != null && yearToColorIndex[y] === undefined) {
      yearToColorIndex[y] = nextIdx % YEAR_COLORS.length;
      nextIdx++;
    }
  });
  const pointColors = serie.map((d) => {
    const y = parseYearFromMesLabel(d.mesLabel);
    const idx = y != null ? yearToColorIndex[y] : 0;
    return YEAR_COLORS[idx % YEAR_COLORS.length];
  });
  return { yearToColorIndex, pointColors };
}

/** Una serie (línea) para el gráfico: etiqueta, datos de 12 meses, color. */
type ColocacionChartSerie = { label: string; data: number[]; color: string };

/** Gráfica de línea con Chart.js (estilo legacy: título arriba, leyenda top, responsive). */
const ColocacionLineChart: React.FC<{
  labels: string[];
  data?: number[];
  title: string;
  color?: string;
  colors?: string[];
  formatValue?: (v: number) => string;
  /** Múltiples líneas (ej. una por año): eje X = labels (12 meses), cada item = una línea. */
  series?: ColocacionChartSerie[];
  /** Leyenda por año: etiquetas (ej. ["2023","2024"]) y sus colores */
  legendLabels?: string[];
  legendColors?: string[];
}> = ({
  labels,
  data = [],
  title,
  color = 'rgb(59, 130, 246)',
  colors,
  formatValue = (v) => v.toLocaleString('es-MX', { maximumFractionDigits: 0 }),
  series,
  legendLabels,
  legendColors,
}) => {
  const multiLine = series && series.length > 0;
  const safeData = data ?? [];
  const pointColors = !multiLine && colors && colors.length === safeData.length ? colors : undefined;

  const chartData = {
    labels,
    datasets: multiLine
      ? series!.map((s) => ({
          label: s.label,
          data: s.data,
          borderColor: s.color,
          backgroundColor: s.color,
          pointBackgroundColor: s.color,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4,
          tension: 0.2,
          fill: false,
        }))
      : [
          {
            label: title,
            data: safeData,
            borderColor: color,
            backgroundColor: pointColors ?? color,
            pointBackgroundColor: pointColors ?? color,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5,
            tension: 0.2,
            fill: false,
          },
        ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: { boxWidth: 12, font: { size: 11 } },
      },
      title: { display: true, text: title, font: { size: 13 } },
      tooltip: {
        callbacks: {
          label: (ctx: { raw: unknown }) => formatValue(Number(ctx.raw)),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxRotation: 45, minRotation: 0, font: { size: 10 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.06)' },
        ticks: {
          font: { size: 10 },
          callback: (value: number | string) => (typeof value === 'number' ? formatValue(value) : value),
        },
      },
    },
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 p-3 sm:p-6 rounded-xl border-2 border-gray-100 shadow-md">
      <div className="relative h-48 sm:h-64 md:h-72 bg-white p-2 sm:p-4 rounded-lg border border-gray-200">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

// Vista Gráfica
const VistaGrafica: React.FC<{ data: ColocacionData; formatCurrency: (v: number) => string }> = ({ data, formatCurrency }) => {
  // Detectar si es modo mensual (frecuencia "2")
  const esMensual = data.frecuencia === "2";

  // Modo diario: una sola sección con tablas por producto (columnas 1-23, filas por mes)
  // Modo mensual: sección con tablas por producto (columnas meses, filas años)
  const blockTitle = `RESUMEN ${data.fechaInicio} - ${data.fechaFin}`;

  const DIAS_HABILES = Array.from({ length: 23 }, (_, i) => i + 1);

  return (
    <div className="space-y-6">
      <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
        <h2 className="text-base sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">{blockTitle}</h2>

        {(data.series[0]?.productos ?? []).map((producto, pIdx) => {
            // Modo diario: cada mes = una línea en el gráfico; tablas usan todas las series (filas = meses)
            const labelsDiario = DIAS_HABILES.map((d) => String(d));
            const getDataForDia = (serie: any[], field: 'cuentaAcumulado' | 'cuenta' | 'montoAutorizadoAcumulado' | 'montoAutorizado' | 'montoTransferenciaAcumulado' | 'montoTransferencia') =>
              DIAS_HABILES.map((dia) => {
                const d = serie?.find((x: any) => x.dia === dia) ?? serie?.[dia - 1];
                const val = d?.[field] ?? d?.cuenta ?? 0;
                return Number(val) || 0;
              });
            const seriesCreditosDiario = !esMensual
              ? (data.series ?? []).map((intervalo: any, i: number) => ({
                  label: `${intervalo.anio}-${String(intervalo.mes_num ?? 1).padStart(2, '0')}`,
                  data: getDataForDia(intervalo.productos?.[pIdx]?.serie ?? [], 'cuentaAcumulado'),
                  color: YEAR_COLORS[i % YEAR_COLORS.length],
                }))
              : undefined;
            const seriesMontoAutorizadoDiario = !esMensual
              ? (data.series ?? []).map((intervalo: any, i: number) => ({
                  label: `${intervalo.anio}-${String(intervalo.mes_num ?? 1).padStart(2, '0')}`,
                  data: getDataForDia(intervalo.productos?.[pIdx]?.serie ?? [], 'montoAutorizadoAcumulado'),
                  color: YEAR_COLORS[i % YEAR_COLORS.length],
                }))
              : undefined;
            const seriesMontoTransferenciaDiario = !esMensual
              ? (data.series ?? []).map((intervalo: any, i: number) => ({
                  label: `${intervalo.anio}-${String(intervalo.mes_num ?? 1).padStart(2, '0')}`,
                  data: getDataForDia(intervalo.productos?.[pIdx]?.serie ?? [], 'montoTransferenciaAcumulado'),
                  color: YEAR_COLORS[i % YEAR_COLORS.length],
                }))
              : undefined;
            const legendLabelsDiario = !esMensual ? (data.series ?? []).map((i: any) => `${i.anio}-${String(i.mes_num ?? 1).padStart(2, '0')}`) : undefined;
            const legendColorsDiario = !esMensual ? (data.series ?? []).map((_, i: number) => YEAR_COLORS[i % YEAR_COLORS.length]) : undefined;

            // Modo mensual: años del rango de fechas (fechaInicio..fechaFin), ordenados ascendente
            const yearsInOrder = esMensual
              ? (() => {
                  const startYear = new Date(data.fechaInicio + 'T12:00:00').getFullYear();
                  const endYear = new Date(data.fechaFin + 'T12:00:00').getFullYear();
                  const years: number[] = [];
                  for (let y = startYear; y <= endYear; y++) years.push(y);
                  return years;
                })()
              : [];
            const yearToColorIndex: Record<number, number> = esMensual
              ? yearsInOrder.reduce((acc, y, i) => ({ ...acc, [y]: i }), {})
              : (getYearColors(producto.serie).yearToColorIndex);

            // Modo mensual: una línea por año, datos = avance del mes (no acumulado)
            const seriesCreditos = esMensual
              ? yearsInOrder.map((year, i) => ({
                  label: String(year),
                  data: getValuesByMonthForYear(producto.serie, year, 'cuenta'),
                  color: YEAR_COLORS[i % YEAR_COLORS.length],
                }))
              : undefined;
            const seriesMontoAutorizado = esMensual
              ? yearsInOrder.map((year, i) => ({
                  label: String(year),
                  data: getValuesByMonthForYear(producto.serie, year, 'montoAutorizado'),
                  color: YEAR_COLORS[i % YEAR_COLORS.length],
                }))
              : undefined;
            const seriesMontoTransferencia = esMensual
              ? yearsInOrder.map((year, i) => ({
                  label: String(year),
                  data: getValuesByMonthForYear(producto.serie, year, 'montoTransferencia'),
                  color: YEAR_COLORS[i % YEAR_COLORS.length],
                }))
              : undefined;

            const legendYearLabels = yearsInOrder.map((y) => String(y));
            const legendYearColors = yearsInOrder.map((y) => YEAR_COLORS[(yearToColorIndex[y] ?? 0) % YEAR_COLORS.length]);
            const cellColor = (d: any) => {
              if (!esMensual) return d.cuenta === 0 ? '#FEF9C3' : 'white';
              const y = parseYearFromMesLabel(d.mesLabel);
              const idx = y != null ? (yearToColorIndex[y] ?? 0) : 0;
              return YEAR_COLORS[idx % YEAR_COLORS.length];
            };
            const cellColorLight = (d: any) => {
              if (!esMensual) return d.cuenta === 0 ? '#FEF9C3' : 'white';
              const y = parseYearFromMesLabel(d.mesLabel);
              const idx = y != null ? (yearToColorIndex[y] ?? 0) : 0;
              return YEAR_COLORS[idx % YEAR_COLORS.length] + '20';
            };
            const cellColorBg10 = (d: any) => {
              if (!esMensual) return d.cuenta === 0 ? '#FEF9C3' : 'white';
              const y = parseYearFromMesLabel(d.mesLabel);
              const idx = y != null ? (yearToColorIndex[y] ?? 0) : 0;
              return YEAR_COLORS[idx % YEAR_COLORS.length] + '10';
            };

            return (
              <div key={pIdx} className="mb-10">
                <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 pb-2 border-b-2 border-gray-300">{producto.producto}</h3>

                <div className="mb-8 space-y-4">
                  <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <h4 className="text-md font-semibold text-gray-800 mb-3">
                      {esMensual ? 'CRÉDITOS COLOCADOS (por mes, no acumulativo)' : 'CRÉDITOS COLOCADOS (Acumulado)'}
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left border font-semibold">{esMensual ? 'Año' : 'Mes'}</th>
                            {esMensual
                              ? MESES_NOMBRES.map((mes, i) => (
                                  <th key={i} className="px-2 py-2 text-center border font-medium">{mes}</th>
                                ))
                              : DIAS_HABILES.map((dia) => (
                                  <th key={dia} className="px-2 py-2 text-center border font-medium">{dia}</th>
                                ))
                            }
                          </tr>
                        </thead>
                        <tbody>
                          {esMensual
                            ? yearsInOrder.map((year) => {
                                const valores = getValuesByMonthForYear(producto.serie, year, 'cuenta');
                                const colorBg = YEAR_COLORS[(yearToColorIndex[year] ?? 0) % YEAR_COLORS.length] + '10';
                                return (
                                  <tr key={year}>
                                    <td className="px-3 py-2 border font-semibold bg-gray-50">{year}</td>
                                    {valores.map((v, mIdx) => (
                                      <td key={mIdx} className="px-2 py-2 text-center border font-medium" style={{ backgroundColor: colorBg }}>{v}</td>
                                    ))}
                                  </tr>
                                );
                              })
                            : (data.series ?? []).map((intervalo: any, rowIdx: number) => {
                                const p = intervalo.productos?.[pIdx];
                                const serie = p?.serie ?? [];
                                const rowLabel = `${intervalo.anio}-${String(intervalo.mes_num ?? 1).padStart(2, '0')}`;
                                return (
                                  <tr key={rowIdx}>
                                    <td className="px-3 py-2 border font-semibold bg-gray-50">{rowLabel}</td>
                                    {DIAS_HABILES.map((dia) => {
                                      const d = serie.find((x: any) => x.dia === dia) ?? serie[dia - 1];
                                      const val = d?.cuentaAcumulado ?? d?.cuenta ?? 0;
                                      const isZero = Number(val) === 0;
                                      return (
                                        <td key={dia} className="px-2 py-2 text-center border font-medium" style={{ backgroundColor: isZero ? '#FEF9C3' : 'white', color: isZero ? '#DC2626' : '#111827' }}>{val}</td>
                                      );
                                    })}
                                  </tr>
                                );
                              })
                          }
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <ColocacionLineChart
                    labels={esMensual ? MESES_EJE_X : labelsDiario}
                    data={undefined}
                    title="CRÉDITOS COLOCADOS"
                    color="#3B82F6"
                    series={esMensual ? seriesCreditos : seriesCreditosDiario}
                    formatValue={(v) => v.toString()}
                    legendLabels={esMensual ? legendYearLabels : legendLabelsDiario}
                    legendColors={esMensual ? legendYearColors : legendColorsDiario}
                  />
                </div>

                <div className="mb-8 space-y-4">
                  <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <h4 className="text-md font-semibold text-gray-800 mb-3">
                      {esMensual ? 'MONTO AUTORIZADO (por mes)' : 'MONTO AUTORIZADO (Acumulado)'}
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left border font-semibold">{esMensual ? 'Año' : 'Mes'}</th>
                            {esMensual
                              ? MESES_NOMBRES.map((mes, i) => (
                                  <th key={i} className="px-2 py-2 text-center border font-medium">{mes}</th>
                                ))
                              : DIAS_HABILES.map((dia) => (
                                  <th key={dia} className="px-2 py-2 text-center border font-medium">{dia}</th>
                                ))
                            }
                          </tr>
                        </thead>
                        <tbody>
                          {esMensual
                            ? yearsInOrder.map((year) => {
                                const valores = getValuesByMonthForYear(producto.serie, year, 'montoAutorizado');
                                const colorBg = YEAR_COLORS[(yearToColorIndex[year] ?? 0) % YEAR_COLORS.length] + '10';
                                return (
                                  <tr key={year}>
                                    <td className="px-3 py-2 border font-semibold bg-gray-50">{year}</td>
                                    {valores.map((v, mIdx) => (
                                      <td key={mIdx} className="px-2 py-2 text-center border text-xs font-medium" style={{ backgroundColor: colorBg }}>{formatCurrency(v)}</td>
                                    ))}
                                  </tr>
                                );
                              })
                            : (data.series ?? []).map((intervalo: any, rowIdx: number) => {
                                const p = intervalo.productos?.[pIdx];
                                const serie = p?.serie ?? [];
                                const rowLabel = `${intervalo.anio}-${String(intervalo.mes_num ?? 1).padStart(2, '0')}`;
                                return (
                                  <tr key={rowIdx}>
                                    <td className="px-3 py-2 border font-semibold bg-gray-50">{rowLabel}</td>
                                    {DIAS_HABILES.map((dia) => {
                                      const d = serie.find((x: any) => x.dia === dia) ?? serie[dia - 1];
                                      const val = d?.montoAutorizadoAcumulado ?? d?.montoAutorizado ?? 0;
                                      const isZero = Number(val) === 0;
                                      return (
                                        <td key={dia} className="px-2 py-2 text-center border text-xs font-medium" style={{ backgroundColor: isZero ? '#FEF9C3' : 'white', color: isZero ? '#DC2626' : '#111827' }}>{formatCurrency(val)}</td>
                                      );
                                    })}
                                  </tr>
                                );
                              })
                          }
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <ColocacionLineChart
                    labels={esMensual ? MESES_EJE_X : labelsDiario}
                    data={undefined}
                    title={esMensual ? 'MONTO AUTORIZADO (Avance por mes)' : 'MONTO AUTORIZADO (Acumulado)'}
                    color="#10B981"
                    series={esMensual ? seriesMontoAutorizado : seriesMontoAutorizadoDiario}
                    formatValue={(v) => formatCurrency(v)}
                    legendLabels={esMensual ? legendYearLabels : legendLabelsDiario}
                    legendColors={esMensual ? legendYearColors : legendColorsDiario}
                  />
                </div>

                <div className="mb-8 space-y-4">
                  <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <h4 className="text-md font-semibold text-gray-800 mb-3">
                      {esMensual ? 'MONTO TRANSFERENCIA (por mes)' : 'MONTO TRANSFERENCIA (Acumulado)'}
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left border font-semibold">{esMensual ? 'Año' : 'Mes'}</th>
                            {esMensual
                              ? MESES_NOMBRES.map((mes, i) => (
                                  <th key={i} className="px-2 py-2 text-center border font-medium">{mes}</th>
                                ))
                              : DIAS_HABILES.map((dia) => (
                                  <th key={dia} className="px-2 py-2 text-center border font-medium">{dia}</th>
                                ))
                            }
                          </tr>
                        </thead>
                        <tbody>
                          {esMensual
                            ? yearsInOrder.map((year) => {
                                const valores = getValuesByMonthForYear(producto.serie, year, 'montoTransferencia');
                                const colorBg = YEAR_COLORS[(yearToColorIndex[year] ?? 0) % YEAR_COLORS.length] + '10';
                                return (
                                  <tr key={year}>
                                    <td className="px-3 py-2 border font-semibold bg-gray-50">{year}</td>
                                    {valores.map((v, mIdx) => (
                                      <td key={mIdx} className="px-2 py-2 text-center border text-xs font-medium" style={{ backgroundColor: colorBg }}>{formatCurrency(v)}</td>
                                    ))}
                                  </tr>
                                );
                              })
                            : (data.series ?? []).map((intervalo: any, rowIdx: number) => {
                                const p = intervalo.productos?.[pIdx];
                                const serie = p?.serie ?? [];
                                const rowLabel = `${intervalo.anio}-${String(intervalo.mes_num ?? 1).padStart(2, '0')}`;
                                return (
                                  <tr key={rowIdx}>
                                    <td className="px-3 py-2 border font-semibold bg-gray-50">{rowLabel}</td>
                                    {DIAS_HABILES.map((dia) => {
                                      const d = serie.find((x: any) => x.dia === dia) ?? serie[dia - 1];
                                      const val = d?.montoTransferenciaAcumulado ?? d?.montoTransferencia ?? 0;
                                      const isZero = Number(val) === 0;
                                      return (
                                        <td key={dia} className="px-2 py-2 text-center border text-xs font-medium" style={{ backgroundColor: isZero ? '#FEF9C3' : 'white', color: isZero ? '#DC2626' : '#111827' }}>{formatCurrency(val)}</td>
                                      );
                                    })}
                                  </tr>
                                );
                              })
                          }
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <ColocacionLineChart
                    labels={esMensual ? MESES_EJE_X : labelsDiario}
                    data={undefined}
                    title={esMensual ? 'MONTO TRANSFERENCIA (Avance por mes)' : 'MONTO TRANSFERENCIA (Acumulado)'}
                    color="#F59E0B"
                    series={esMensual ? seriesMontoTransferencia : seriesMontoTransferenciaDiario}
                    formatValue={(v) => formatCurrency(v)}
                    legendLabels={esMensual ? legendYearLabels : legendLabelsDiario}
                    legendColors={esMensual ? legendYearColors : legendColorsDiario}
                  />
                </div>
              </div>
            );
          })}
        </div>
    </div>
  );
};

// Vista Tabular
const VistaTabular: React.FC<{
  data: ColocacionTabularData;
  formatCurrency: (v: number) => string;
  formatPercent: (v: number, t: number) => string;
  formatSeguro: (v: string | number) => string;
}> = ({ data, formatCurrency, formatPercent, formatSeguro }) => {
  const productos = Object.keys(data.datosPorProducto);
  const montosSeguro = ['0', '25000', '50000', '75000', '100000'];

  return (
    <div className="space-y-6">
      {/* Resumen por Producto */}
      <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
        <h2 className="text-base sm:text-xl font-bold text-gray-900 mb-4">RESUMEN POR PRODUCTO</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left border font-semibold">PRODUCTO</th>
                <th className="px-4 py-3 text-right border font-semibold">CRÉDITOS</th>
                <th className="px-4 py-3 text-right border font-semibold">AUTORIZADO</th>
                <th className="px-4 py-3 text-right border font-semibold">TRANSFERENCIA</th>
                <th className="px-4 py-3 text-right border font-semibold">COMISIONES</th>
                <th className="px-4 py-3 text-right border font-semibold">SEGUROS FINANCIADOS</th>
              </tr>
            </thead>
            <tbody>
              {productos.map((producto) => {
                const totales = data.totalesPorProducto[producto];
                return (
                  <tr key={producto}>
                    <td className="px-4 py-2 border font-medium">{producto}</td>
                    <td className="px-4 py-2 border text-right">{totales.totalCreditos}</td>
                    <td className="px-4 py-2 border text-right">{formatCurrency(totales.montoTotal)}</td>
                    <td className="px-4 py-2 border text-right">{formatCurrency(totales.montoTotalTransferencia)}</td>
                    <td className="px-4 py-2 border text-right">{formatCurrency(totales.montoTotalComisiones)}</td>
                    <td className="px-4 py-2 border text-right">{formatCurrency(totales.montoTotalSegurosFinanciados)}</td>
                  </tr>
                );
              })}
              <tr className="bg-gray-100 font-bold">
                <td className="px-4 py-2 border">TOTAL</td>
                <td className="px-4 py-2 border text-right">{data.granTotal.totalCreditos}</td>
                <td className="px-4 py-2 border text-right">{formatCurrency(data.granTotal.montoTotal)}</td>
                <td className="px-4 py-2 border text-right">{formatCurrency(data.granTotal.montoTotalTransferencia)}</td>
                <td className="px-4 py-2 border text-right">{formatCurrency(data.granTotal.montoTotalComisiones)}</td>
                <td className="px-4 py-2 border text-right">{formatCurrency(data.granTotal.montoTotalSegurosFinanciados)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Resumen por Asesor/Producto */}
      {productos.map((producto) => {
        const asesores = Object.keys(data.datosPorProducto[producto]);
        const totales = data.totalesPorProducto[producto];

        return (
          <div key={producto} className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
            <h2 className="text-base sm:text-xl font-bold text-gray-900 mb-4">RESUMEN POR ASESOR / PRODUCTO: {producto}</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left border font-semibold">ASESOR</th>
                    <th className="px-2 py-2 text-right border font-semibold">CRÉDITOS</th>
                    <th className="px-2 py-2 text-right border font-semibold">AUTORIZADO</th>
                    <th className="px-2 py-2 text-right border font-semibold">TRANSFERENCIA</th>
                    <th className="px-2 py-2 text-right border font-semibold">NUEVOS</th>
                    <th className="px-2 py-2 text-right border font-semibold">% #NUEVOS</th>
                    <th className="px-2 py-2 text-right border font-semibold">% $NUEVOS</th>
                    <th className="px-2 py-2 text-right border font-semibold">COMISIONES</th>
                    {montosSeguro.map((monto) => (
                      <th key={monto} className="px-2 py-2 text-right border font-semibold">{monto === '0' ? 'SIN SEG' : formatSeguro(monto)}</th>
                    ))}
                    <th className="px-2 py-2 text-right border font-semibold">TOTAL SEG</th>
                    <th className="px-2 py-2 text-right border font-semibold">FINANCIADO</th>
                  </tr>
                </thead>
                <tbody>
                  {asesores.map((asesor) => {
                    const datos = data.datosPorProducto[producto][asesor];
                    const totalSeguros = Object.values(datos.seguros).reduce((sum, s) => sum + s.total, 0);
                    const totalFinanciado = Object.values(datos.seguros).reduce((sum, s) => sum + s.financiado, 0);

                    return (
                      <tr key={asesor}>
                        <td className="px-2 py-2 border font-medium">{asesor}</td>
                        <td className="px-2 py-2 border text-right">{datos.totalCreditos}</td>
                        <td className="px-2 py-2 border text-right">{formatCurrency(datos.montoTotal)}</td>
                        <td className="px-2 py-2 border text-right">{formatCurrency(datos.montoTotalTransferencia)}</td>
                        <td className="px-2 py-2 border text-right">{datos.totalCreditosNuevos}</td>
                        <td className="px-2 py-2 border text-right">{formatPercent(datos.totalCreditosNuevos, datos.totalCreditos)}</td>
                        <td className="px-2 py-2 border text-right">{formatPercent(datos.montoTotalTransferenciaNuevos, datos.montoTotalTransferencia)}</td>
                        <td className="px-2 py-2 border text-right">{formatCurrency(datos.montoTotalComisiones)}</td>
                        {montosSeguro.map((monto) => (
                          <td key={monto} className="px-2 py-2 border text-right">{datos.seguros[monto]?.total || 0}</td>
                        ))}
                        <td className="px-2 py-2 border text-right">{totalSeguros}</td>
                        <td className="px-2 py-2 border text-right">{formatCurrency(totalFinanciado)}</td>
                      </tr>
                    );
                  })}
                  <tr className="bg-gray-100 font-bold">
                    <td className="px-2 py-2 border">TOTAL</td>
                    <td className="px-2 py-2 border text-right">{totales.totalCreditos}</td>
                    <td className="px-2 py-2 border text-right">{formatCurrency(totales.montoTotal)}</td>
                    <td className="px-2 py-2 border text-right">{formatCurrency(totales.montoTotalTransferencia)}</td>
                    <td className="px-2 py-2 border text-right">{totales.totalCreditosNuevos}</td>
                    <td className="px-2 py-2 border text-right">{formatPercent(totales.totalCreditosNuevos, totales.totalCreditos)}</td>
                    <td className="px-2 py-2 border text-right">{formatPercent(totales.montoTotalTransferenciaNuevos, totales.montoTotalTransferencia)}</td>
                    <td className="px-2 py-2 border text-right">{formatCurrency(totales.montoTotalComisiones)}</td>
                    {montosSeguro.map((monto) => {
                      const total = asesores.reduce((sum, a) => sum + (data.datosPorProducto[producto][a].seguros[monto]?.total || 0), 0);
                      return <td key={monto} className="px-2 py-2 border text-right">{total}</td>;
                    })}
                    <td className="px-2 py-2 border text-right">{asesores.reduce((sum, a) => sum + Object.values(data.datosPorProducto[producto][a].seguros).reduce((s, seg) => s + seg.total, 0), 0)}</td>
                    <td className="px-2 py-2 border text-right">{formatCurrency(totales.montoTotalSegurosFinanciados)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Detalle por Cliente */}
      {productos.map((producto) => {
        const asesores = Object.keys(data.datosPorProducto[producto]);

        return (
          <div key={producto} className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
            <h2 className="text-base sm:text-xl font-bold text-gray-900 mb-4">DETALLE POR CLIENTE: {producto}</h2>
            {asesores.map((asesor) => {
              const datos = data.datosPorProducto[producto][asesor];

              return (
                <div key={asesor} className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{asesor}</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-2 text-left border font-semibold">#</th>
                          <th className="px-2 py-2 text-left border font-semibold">PRODUCTO</th>
                          <th className="px-2 py-2 text-left border font-semibold">CONTRATO</th>
                          <th className="px-2 py-2 text-left border font-semibold">NUM CONTROL</th>
                          <th className="px-2 py-2 text-left border font-semibold">CLIENTE</th>
                          <th className="px-2 py-2 text-left border font-semibold">EMPRESA</th>
                          <th className="px-2 py-2 text-left border font-semibold">ESTATUS</th>
                          <th className="px-2 py-2 text-right border font-semibold">AUTORIZADO</th>
                          <th className="px-2 py-2 text-right border font-semibold">TRANSFERIDO</th>
                          <th className="px-2 py-2 text-right border font-semibold">COMISIÓN</th>
                          <th className="px-2 py-2 text-right border font-semibold">SEG FIN</th>
                          <th className="px-2 py-2 text-center border font-semibold">SEGURO</th>
                          <th className="px-2 py-2 text-center border font-semibold">EXPEDIENTE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {datos.solicitudes.map((sol, idx) => (
                          <tr key={sol.id}>
                            <td className="px-2 py-2 border">{idx + 1}</td>
                            <td className="px-2 py-2 border">{producto}</td>
                            <td className="px-2 py-2 border">{sol.fechaContratoImpreso}</td>
                            <td className="px-2 py-2 border">{sol.numeroControl}</td>
                            <td className="px-2 py-2 border">{sol.cliente}</td>
                            <td className="px-2 py-2 border">{sol.empresa || '-'}</td>
                            <td className="px-2 py-2 border">{sol.estatus || '-'}</td>
                            <td className="px-2 py-2 border text-right">{formatCurrency(sol.montoAutorizado)}</td>
                            <td className="px-2 py-2 border text-right">{formatCurrency(sol.montoTransferencia)}</td>
                            <td className="px-2 py-2 border text-right">{formatCurrency(sol.comision)}</td>
                            <td className="px-2 py-2 border text-right">{formatCurrency(sol.seguroFinanciado)}</td>
                            <td className="px-2 py-2 border text-center">{formatSeguro(sol.seguro)}</td>
                            <td className="px-2 py-2 border text-center">{sol.expedienteResguardoStatus || 'Comercial'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default ColocacionManagement;
